import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  CheckCircleIcon,
  PlayIcon,
  ClockIcon,
  AcademicCapIcon,
  ChartBarIcon,
  StarIcon,
  EyeIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';
import { PROGRESS_STATUS } from '../../services/programsService';
import { AnimatePresence } from 'framer-motion';

const ProgressTracker = ({ programs, userProgress, onLessonStart, onLessonComplete, userRole }) => {
  const [expandedProgram, setExpandedProgram] = useState(null);

  const getStatusIcon = status => {
    switch (status) {
      case PROGRESS_STATUS.COMPLETED:
        return <CheckCircleIcon className="h-5 w-5 text-green-600" />;
      case PROGRESS_STATUS.IN_PROGRESS:
        return <PlayIcon className="h-5 w-5 text-blue-600" />;
      default:
        return <ClockIcon className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusColor = status => {
    switch (status) {
      case PROGRESS_STATUS.COMPLETED:
        return 'bg-green-100 text-green-800';
      case PROGRESS_STATUS.IN_PROGRESS:
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getProgressColor = percentage => {
    if (percentage >= 80) return 'bg-green-500';
    if (percentage >= 60) return 'bg-yellow-500';
    if (percentage >= 40) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const toggleProgramExpansion = programId => {
    setExpandedProgram(expandedProgram === programId ? null : programId);
  };

  const formatDate = timestamp => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-900">Progress Overview</h2>
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <AcademicCapIcon className="h-4 w-4" />
          <span>{programs.length} Programs</span>
        </div>
      </div>

      <div className="rounded-lg bg-gradient-to-r from-blue-50 to-indigo-50 p-6">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Overall Progress</h3>
          <ChartBarIcon className="h-6 w-6 text-blue-600" />
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">
              {
                programs.filter(p => userProgress[p.id]?.status === PROGRESS_STATUS.COMPLETED)
                  .length
              }
            </div>
            <div className="text-sm text-gray-600">Completed</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-yellow-600">
              {
                programs.filter(p => userProgress[p.id]?.status === PROGRESS_STATUS.IN_PROGRESS)
                  .length
              }
            </div>
            <div className="text-sm text-gray-600">In Progress</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-600">
              {
                programs.filter(
                  p =>
                    !userProgress[p.id] ||
                    userProgress[p.id]?.status === PROGRESS_STATUS.NOT_STARTED
                ).length
              }
            </div>
            <div className="text-sm text-gray-600">Not Started</div>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {programs.map((program, index) => {
          const progress = userProgress[program.id];
          const progressPercentage = progress?.progressPercentage || 0;
          const status = progress?.status || PROGRESS_STATUS.NOT_STARTED;
          const isExpanded = expandedProgram === program.id;

          return (
            <motion.div
              key={program.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
              className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm"
            >
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {getStatusIcon(status)}
                    <div>
                      <h3 className="font-semibold text-gray-900">{program.title}</h3>
                      <p className="text-sm text-gray-600">{program.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span
                      className={`rounded-full px-2 py-1 text-xs font-medium ${getStatusColor(status)}`}
                    >
                      {status.replace('-', ' ')}
                    </span>
                    <button
                      onClick={() => toggleProgramExpansion(program.id)}
                      className="rounded-md p-1 transition-colors hover:bg-gray-100"
                    >
                      <ArrowRightIcon
                        className={`h-4 w-4 text-gray-500 transition-transform ${
                          isExpanded ? 'rotate-90' : ''
                        }`}
                      />
                    </button>
                  </div>
                </div>

                <div className="mt-4">
                  <div className="mb-2 flex items-center justify-between text-sm">
                    <span className="text-gray-600">Progress</span>
                    <span className="font-medium">{progressPercentage}%</span>
                  </div>
                  <div className="h-2 w-full rounded-full bg-gray-200">
                    <motion.div
                      className={`h-2 rounded-full transition-all duration-500 ${getProgressColor(progressPercentage)}`}
                      initial={{ width: 0 }}
                      animate={{ width: `${progressPercentage}%` }}
                    />
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-4 text-sm md:grid-cols-4">
                  <div>
                    <span className="text-gray-600">Duration:</span>
                    <span className="ml-1 font-medium">{program.duration}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Difficulty:</span>
                    <span className="ml-1 font-medium">{program.difficulty}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Last Activity:</span>
                    <span className="ml-1 font-medium">
                      {progress?.lastActivityAt ? formatDate(progress.lastActivityAt) : 'Never'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Started:</span>
                    <span className="ml-1 font-medium">
                      {progress?.createdAt ? formatDate(progress.createdAt) : 'Not started'}
                    </span>
                  </div>
                </div>
              </div>

              <AnimatePresence>
                {isExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="border-t border-gray-200"
                  >
                    <div className="bg-gray-50 p-4">
                      <h4 className="mb-3 font-medium text-gray-900">Lesson Progress</h4>

                      {progress?.lessons ? (
                        <div className="space-y-2">
                          {Object.entries(progress.lessons).map(([lessonId, lessonData]) => (
                            <div
                              key={lessonId}
                              className="flex items-center justify-between rounded-lg border border-gray-200 bg-white p-3"
                            >
                              <div className="flex items-center gap-3">
                                {lessonData.completed ? (
                                  <CheckCircleIcon className="h-5 w-5 text-green-600" />
                                ) : lessonData.started ? (
                                  <PlayIcon className="h-5 w-5 text-blue-600" />
                                ) : (
                                  <ClockIcon className="h-5 w-5 text-gray-400" />
                                )}
                                <div>
                                  <div className="font-medium text-gray-900">Lesson {lessonId}</div>
                                  <div className="text-sm text-gray-600">
                                    {lessonData.completed
                                      ? `Completed on ${formatDate(lessonData.completedAt)}`
                                      : lessonData.started
                                        ? `Started on ${formatDate(lessonData.startedAt)}`
                                        : 'Not started'}
                                  </div>
                                </div>
                              </div>

                              <div className="flex items-center gap-2">
                                {!lessonData.completed && (
                                  <>
                                    {!lessonData.started && (
                                      <button
                                        onClick={() => onLessonStart(lessonId)}
                                        className="rounded-md bg-blue-600 px-3 py-1 text-xs text-white transition-colors hover:bg-blue-700"
                                      >
                                        Start
                                      </button>
                                    )}
                                    {lessonData.started && (
                                      <button
                                        onClick={() => onLessonComplete(lessonId)}
                                        className="rounded-md bg-green-600 px-3 py-1 text-xs text-white transition-colors hover:bg-green-700"
                                      >
                                        Complete
                                      </button>
                                    )}
                                  </>
                                )}
                                <EyeIcon className="h-4 w-4 text-gray-400" />
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="py-6 text-center">
                          <AcademicCapIcon className="mx-auto mb-2 h-12 w-12 text-gray-400" />
                          <p className="text-gray-600">No lessons available yet</p>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}
      </div>

      {programs.length === 0 && (
        <div className="py-12 text-center">
          <AcademicCapIcon className="mx-auto mb-4 h-16 w-16 text-gray-400" />
          <h3 className="mb-2 text-lg font-medium text-gray-900">No Programs Available</h3>
          <p className="text-gray-600">Check back later for new training programs.</p>
        </div>
      )}
    </div>
  );
};

export default ProgressTracker;
